import { useState } from "react";
import { Quote } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { TESTIMONIALS, SAMPLES } from "@/data/stories";
import { STATS, whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const REGIONS = ["All", "India", "Europe", "Middle East"];

export default function SuccessStories() {
  const [region, setRegion] = useState("All");
  const filtered = region === "All" ? TESTIMONIALS : TESTIMONIALS.filter((t) => t.region === region);

  return (
    <div data-testid="success-stories-page">
      <Seo
        title="Success Stories | MBA Project & Thesis Writing Help"
        description="Real feedback from students across India, Europe, and the Middle East, plus anonymised excerpts from delivered MBA, law, and PhD work."
      />
      <PageHero
        eyebrow="Success stories"
        lines={["Real students.", "Real submissions.", "Real results."]}
        sub="Every story below comes from a student we've worked with — names shortened and details anonymised to protect their privacy. The work speaks for itself."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stories-stats">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm">
              <p className="font-display font-extrabold text-3xl md:text-4xl text-[#2563EB] leading-none">{s.value}</p>
              <p className="mt-2 text-xs md:text-sm text-slate-500 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <Reveal>
          <Eyebrow>Student feedback</Eyebrow>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
            What students told us after submission.
          </h2>
        </Reveal>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="region-filter">
          {REGIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              data-testid={`region-filter-${r.toLowerCase().replace(" ", "-")}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors duration-200 ${
                region === r
                  ? "bg-[#2563EB] text-white border-[#2563EB]"
                  : "bg-white text-slate-600 border-slate-300 hover:border-[#2563EB] hover:text-[#2563EB]"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stories-grid">
          {filtered.map((t) => (
            <div
              key={t.name + t.meta}
              className="flex flex-col h-full bg-white border border-slate-200 rounded-2xl p-7 shadow-sm transition-shadow duration-300 hover:shadow-lg"
              data-testid={`story-card-${t.name.toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <Quote className="h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-widest bg-blue-50 text-[#2563EB] border border-blue-100 rounded-full px-3 py-1 font-semibold">
                  {t.region}
                </span>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 flex-1">{t.quote}</p>
              <div className="mt-6 pt-5 border-t border-slate-100">
                <p className="font-display font-bold text-base text-slate-900">{t.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t.meta}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F5F8FF] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <Reveal>
            <Eyebrow>Sample work</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Excerpts from delivered work.
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl">
              Anonymised passages from real deliveries. Names, institutions, and identifying details removed to protect client confidentiality.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SAMPLES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="h-full">
                <div className="flex flex-col h-full bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid={`stories-sample-${i}`}>
                  <div className="overflow-hidden h-40">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold">{s.tag}</span>
                    <h3 className="mt-3 font-display font-bold text-lg tracking-tight">{s.title}</h3>
                    <p className="mt-4 text-sm text-slate-500 leading-relaxed italic border-l-2 border-blue-100 pl-4 flex-1">{s.excerpt}</p>
                    <p className="mt-5 text-xs text-slate-400 uppercase tracking-widest">Anonymised excerpt</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 text-center sm:text-left">
        <Reveal>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">
            Your story could be next.
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl">
            Send your topic and deadline on WhatsApp and get a free, fixed quote — usually within a few hours.
          </p>
          <a
            href={whatsappWithText("Hi, I read your success stories. Can you help with my project?")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stories-whatsapp-cta"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-green-500/25 transition-transform duration-200 hover:-translate-y-0.5"
          >
            <WhatsAppIcon className="h-5 w-5" /> Start Your Story
          </a>
        </Reveal>
      </section>
    </div>
  );
}
